/**
 * ticket-inapp.stencil-mark — `<tkt-mark>`, the element both of the stencil
 * card's uploaded-image slots go through: the hero's event wordmark and the
 * footer's operator brand mark (ticket-inapp.stencil.template.ts).
 *
 * WHAT IT EXISTS FOR IS THE FAILED LOAD, NOT THE ABSENT ONE. An absent upload
 * never reaches here — the composition renders its generated wordmark instead.
 * A PRESENT url that does not resolve is the case a bare `<img>` answers with
 * the browser's own broken-image glyph and its alt text, which is what a buyer
 * then sees on their ticket, and what a signed PDF freezes forever.
 * `ticket.props.ts` promises "never a broken image" for both `logoUrl` fields;
 * this element is what makes that a mechanism rather than a sentence. On the
 * image's own `error` — a 404, a url that resolves to something that is not an
 * image, a decode failure — it drops the image and renders the caller's
 * `fallback` instead, which is the same generated wordmark an absent upload
 * would have drawn.
 *
 * THE FALLBACK IS MOUNTED ONLY AFTER THE FAILURE, never rendered hidden
 * alongside the image: the generated wordmark is `<tkt-fitted-wordmark>`,
 * which measures its own rendered width to fit itself, and an element that
 * measures itself while `display: none` reads zero and fits to nothing. Mounted
 * fresh at the moment of failure it measures a real box, exactly as it does on
 * a card that never had an upload at all.
 *
 * LIGHT DOM (`createRenderRoot` returns `this`), for the reason
 * `<tkt-fitted-wordmark>` gives: the card carries its own co-located
 * stylesheet, which a shadow root would not see without separately adopting
 * it.
 *
 * THE ONE FAILURE IT CANNOT SEE is an image that loads perfectly and carries
 * no alpha channel — a jpeg wordmark on its own opaque field. Nothing fires,
 * because nothing failed; the footer's ink filter then paints that whole field
 * (see `.tkt-stencil-brand-logo`, ticket-inapp.styles.ts). Reading the pixels
 * back would answer it, and cannot be done here: the canvas that would be read
 * is tainted by any cross-origin image, which every Storage-hosted mark is.
 * That case belongs to whatever accepts the operator's upload.
 */
import { html, LitElement, type TemplateResult } from "lit";

const MARK_TAG = "tkt-mark";

export class TktMark extends LitElement {
	static override properties = {
		src: { type: String },
		alt: { type: String },
		markClass: { type: String, attribute: "mark-class" },
		markStyle: { type: String, attribute: "mark-style" },
		fallback: { attribute: false },
		unresolved: { type: Boolean, state: true },
	};

	declare src: string;
	declare alt: string;
	declare markClass: string;
	declare markStyle: string;
	declare fallback: TemplateResult | null;
	declare unresolved: boolean;

	constructor() {
		super();
		this.src = "";
		this.alt = "";
		this.markClass = "";
		this.markStyle = "";
		this.fallback = null;
		this.unresolved = false;
	}

	override createRenderRoot(): this {
		return this;
	}

	override render(): TemplateResult {
		if (this.unresolved) return html`${this.fallback}`;
		return html`<img
      src=${this.src}
      alt=${this.alt}
      class=${this.markClass}
      style=${this.markStyle}
      @error=${this.#onError}
    />`;
	}

	#onError = (): void => {
		this.unresolved = true;
	};
}

if (!customElements.get(MARK_TAG)) {
	customElements.define(MARK_TAG, TktMark);
}

declare global {
	interface HTMLElementTagNameMap {
		[MARK_TAG]: TktMark;
	}
}

/**
 * One uploaded mark, with the generated wordmark it falls back to when the
 * upload cannot be fetched. `markClass` and `markStyle` are the image's own
 * presentation, which stays with the composition that knows the slot's
 * geometry; this function only wires the failure path.
 *
 * The host is `display: contents` so it contributes no box of its own — the
 * image (or, after a failure, the fallback) sits in the composition's flex
 * column exactly where it would without this wrapper, so the slot's height
 * budget is the same whichever of the two is drawn.
 */
export function renderUploadedMark(
	src: string,
	{
		alt,
		markClass,
		markStyle,
		fallback,
	}: {
		alt: string;
		markClass: string;
		markStyle: string;
		fallback: TemplateResult;
	},
): TemplateResult {
	return html`<tkt-mark
    style="display: contents"
    src=${src}
    alt=${alt}
    mark-class=${markClass}
    mark-style=${markStyle}
    .fallback=${fallback}
  ></tkt-mark>`;
}
